import { useQuery } from 'react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useOnboardHSAConfigAtom,
  useOnboardNewCustomerAtom,
} from '../../atoms';

import {
  createCustomer,
  createHSAConfig,
  getOnboardingBaseData,
} from '../../api';

import { DEFAULT_STALE_TIME } from '../../constants';

// Components
import Select, {
  SingleSelection,
  sortSelectOptions,
} from '../../components/Select';
import NewClientModal from './NewClientModal';
import OnboardContainer from './OnboardContainer';
import TooltipPopover from '../../components/TooltipPopover';
import NewLineOfBusinessModal from './NewLineOfBusinessModal';

// Types
import { NewClientData, NewLOBData } from 'types';

type FormState = {
  agency: SingleSelection;
  client: SingleSelection;
  lob: SingleSelection;
  market: SingleSelection;
  region: string;
};

const DEFAULT_FORM: FormState = {
  agency: null,
  client: null,
  lob: null,
  market: null,
  region: '',
};

function NewCustomerForm() {
  const { t } = useTranslation();

  const [addLOB, setAddLOB] = useState(false);
  const [addClient, setAddClient] = useState(false);

  const [, setHSAConfig] = useOnboardHSAConfigAtom();
  const [, setNewCustomer] = useOnboardNewCustomerAtom();

  const [formState, setFormState] = useState({ ...DEFAULT_FORM });

  const { data: agencies, refetch } = useQuery({
    queryKey: ['new-customer', 'agencies'],
    queryFn: async () => {
      const data = await getOnboardingBaseData();
      return data.agencies;
    },
    staleTime: DEFAULT_STALE_TIME,
  });

  // Agency Options
  const agencyOptions = useMemo(() => {
    const options =
      agencies?.map(({ name, id }) => ({
        label: name,
        value: id.toString(),
      })) || [];
    return sortSelectOptions(options);
  }, [agencies]);

  // Client Data & Options
  const { clients, clientOptions } = useMemo(() => {
    const selectedAgency = agencies?.find(
      (agency) => agency.id === parseInt(formState.agency?.value || '')
    );

    const options =
      selectedAgency?.clients?.map(({ name, id }) => ({
        label: name,
        value: id.toString(),
      })) || [];

    return {
      clients: selectedAgency?.clients || [],
      clientOptions: sortSelectOptions(options),
    };
  }, [formState.agency, agencies]);

  // Market/Lines of Business data & options
  const { markets, marketOptions, linesOfBusinessOptions } = useMemo(() => {
    const selectedClient = clients.find(
      (client) => client.id === parseInt(formState.client?.value || '')
    );

    const selectedLOB = selectedClient?.lines_of_business.find(
      (lob) => lob.id === parseInt(formState.lob?.value || '')
    );

    const available_markets =
      selectedLOB?.available_markets || selectedClient?.available_markets;

    const marketOptions =
      available_markets?.map(({ name, id }) => ({
        label: name,
        value: id.toString(),
      })) || [];

    const linesOfBusinessOptions =
      selectedClient?.lines_of_business?.map(({ name, id }) => ({
        label: name,
        value: id.toString(),
      })) || [];

    return {
      marketOptions: sortSelectOptions(marketOptions),
      linesOfBusinessOptions: sortSelectOptions(linesOfBusinessOptions),
      markets: available_markets || [],
      linesOfBusiness: selectedClient?.lines_of_business || [],
    };
  }, [clients, formState.client, formState.lob]);

  // Handle Agency selection
  const onChangeAgency = (selection: SingleSelection) => {
    setFormState({ ...DEFAULT_FORM, agency: selection });
  };

  // Handle Client selection
  const onChangeClient = (selection: SingleSelection) => {
    setFormState((prev) => ({
      ...DEFAULT_FORM,
      agency: prev.agency,
      client: selection,
    }));
  };

  // Handle Line of Business selection
  const onChangeLOB = (selection: SingleSelection) => {
    setFormState((prev) => ({
      ...prev,
      lob: selection,
      market: null,
      region: '',
    }));
  };

  // Handle Market selection
  const onChangeMarket = (selection: SingleSelection) => {
    const selectedMarket = markets.find(
      (market) => market.id === parseInt(selection?.value || '')
    );
    setFormState((prev) => ({
      ...prev,
      market: selection,
      region: selectedMarket?.region.name || '',
    }));
  };

  const onClickAddClient = () => {
    setAddClient(true);
    if (formState.client) {
      onChangeClient(null);
    }
  };

  const onClickAddLOB = () => {
    setAddLOB(true);
    if (formState.lob) {
      onChangeLOB(null);
    }
  };

  const onSubmitClient = async (newClient: NewClientData | null) => {
    if (newClient) {
      onChangeClient({ label: newClient.name, value: newClient.id.toString() });
      refetch();
    }
    setAddClient(false);
  };

  const onSubmitLOB = (newLOB: NewLOBData | null) => {
    if (newLOB) {
      refetch();
      onChangeLOB({ label: newLOB.name, value: newLOB.id.toString() });
    }
    setAddLOB(false);
  };

  const onSubmitCustomer = async () => {
    if (!formState.agency?.value) {
      throw new Error('Agency is required.');
    }
    if (!formState.client?.value) {
      throw new Error('Client is required.');
    }
    if (!formState.market?.value) {
      throw new Error('Client is required.');
    }
    if (!formState.region) {
      throw new Error('Regoin is required.');
    }

    // Local Development
    // return { message: 'Submitting Account Details' }

    const res = await createCustomer({
      agency_id: parseInt(formState.agency.value),
      client_id: parseInt(formState.client.value),
      line_of_business_id: formState.lob?.value
        ? parseInt(formState.lob.value)
        : undefined,
      market: formState.market.label,
      region: formState.region,
    });

    const { data: hsaConfig } = await createHSAConfig(res.data.customer_id);

    setHSAConfig(hsaConfig);
    setNewCustomer(res.data);
    setFormState({ ...DEFAULT_FORM });
    return res;
  };

  // JSX
  return (
    <OnboardContainer
      header="Account Details"
      onSubmit={onSubmitCustomer}
      isDisabled={!formState.region}
    >
      <>
        <Select
          name="agency"
          label="Agency"
          options={agencyOptions}
          value={formState.agency}
          onChange={onChangeAgency}
          required
        />
        <div className="relative">
          <button
            type="button"
            className="absolute top-0 right-0 -translate-y-2 border border-solid border-gray400 text-gray400 text-sm px-2 rounded-full flex items-center gap-1"
            onClick={onClickAddClient}
          >
            <span className="text-xl leading-none">+</span>Add
          </button>
          <Select
            name="client"
            label="Client"
            options={clientOptions}
            value={formState.client}
            onChange={onChangeClient}
            required
          />
        </div>
        <div className="relative">
          <button
            type="button"
            className="absolute top-0 right-0 -translate-y-2 border border-solid border-gray400 text-gray400 text-sm px-2 rounded-full flex items-center gap-1"
            onClick={onClickAddLOB}
          >
            <span className="text-xl leading-none">+</span>Add
          </button>
          <Select
            name="lineOfBusiness"
            label="Line of Business"
            options={linesOfBusinessOptions}
            value={formState.lob}
            onChange={onChangeLOB}
            required={!!linesOfBusinessOptions.length}
          />
        </div>
        <TooltipPopover
          complexContent={<div className="w-96">{t('onboarding.market')}</div>}
          type="info"
          side="top"
          className="top-1 left-16"
        />
        <Select
          name="market"
          label="Market"
          options={marketOptions}
          value={formState.market}
          onChange={onChangeMarket}
          required
        />
        {formState.market ? (
          <div>
            <label className="block font-bold mb-2">Region</label>
            <input
              name="region"
              className="bg-gray-200 w-full border border-gray200 rounded p-2 text-gray600 text-sm cursor-not-allowed"
              value={formState.region}
              disabled={true}
              required
            />
          </div>
        ) : null}

        {/* --- Add Client Modal --- */}
        {addClient ? (
          <NewClientModal
            agencyOptions={agencyOptions}
            agencyValue={formState.agency}
            onChangeAgency={onChangeAgency}
            onCancel={() => setAddClient(false)}
            onSubmit={onSubmitClient}
          />
        ) : null}

        {/* --- Add LOB Modal --- */}
        {addLOB ? (
          <NewLineOfBusinessModal
            agencyOptions={agencyOptions}
            agencyValue={formState.agency}
            clientOptions={clientOptions}
            clientValue={formState.client}
            onChangeAgency={onChangeAgency}
            onChangeClient={onChangeClient}
            onCancel={() => setAddLOB(false)}
            onSubmit={onSubmitLOB}
          />
        ) : null}
      </>
    </OnboardContainer>
  );
}

export default NewCustomerForm;
